import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
	createRawRichTextContentStateWithHtmlStringValue,
	type IEmailMessageCompose,
	ImpersonationBroker,
	type IRawRichTextContentState,
	type ISendEmailResponse,
	type ITemplateReference,
	type ResourceVisibility,
	ScheduleCriteria,
} from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

export interface SendEmailVariables {
	/** Lead/company id — used to invalidate the lead query after send. */
	companyId: string;
	/** Recipient contact id. */
	contactId: string;
	/** Preferred recipient email; falls back to the contact's primary when omitted. */
	toEmail?: string;
	/** Subject line. */
	subject: string;
	/** HTML body from the rich-text editor. */
	bodyHtml: string;
	/** CC email addresses. Defaults to none. */
	cc?: string[];
	/**
	 * Signature template id to apply server-side. `'default'` (the default here)
	 * applies the user's default signature; a specific id applies that signature;
	 * `Api.EmptyEmailSignatureTemplateId` sends with no signature.
	 */
	signatureTemplateId?: string;
	/**
	 * Whether to log the send as a note on the contact's timeline. Mirrors the
	 * legacy "Save a copy of this email as a note" advanced setting. Defaults true.
	 */
	saveAsNote?: boolean;
	/**
	 * Note visibility when `saveAsNote` is true: `'all'` (shared) or `'admin'`
	 * (private). Mirrors the legacy "Shared note" toggle. Defaults `'all'`.
	 */
	noteVisibility?: ResourceVisibility;
	/** The template the body was written from; the server fills its `{{citations}}` field from it. */
	templateReference?: ITemplateReference;
}

/**
 * Sends a one-off email to a single contact from the focused-view composer.
 *
 * A clean react-query replacement for the legacy MobX `EmailMessageViewModel.send`
 * (`extViewmodels/ViewModels.ts:5712`): POSTs `email` with a multipart `value`
 * field carrying the `IEmailMessageCompose` JSON (matching `toJs`,
 * `ViewModels.ts:5577-5653`). Minimal single immediate send — no
 * automations, attachments, or CC (those are deferred).
 *
 * Body HTML is wrapped into `IRawRichTextContentState` (the `<lev-content>`
 * shape the server expects); `signatureTemplateId: 'default'` applies the
 * user's default signature server-side; `saveAsNote` mirrors the legacy default
 * so the send is logged on the contact's timeline.
 */
export function useSendEmail(): UseMutationResult<ISendEmailResponse, Error, SendEmailVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<ISendEmailResponse, Error, SendEmailVariables>({
		mutationFn: async ({
			contactId,
			toEmail,
			subject,
			bodyHtml,
			cc = [],
			signatureTemplateId = 'default',
			saveAsNote = true,
			noteVisibility = 'all',
			templateReference = null,
		}) => {
			const content: IRawRichTextContentState = createRawRichTextContentStateWithHtmlStringValue(bodyHtml ?? '');

			const compose: Partial<IEmailMessageCompose> = {
				subject,
				content,
				contacts: [{ contactId, preferredEmailAddress: toEmail || null }],
				contactIdsToOmit: [],
				carbonCopy: { cc, bcc: [] },
				// Explicit 'default' applies the user's default signature server-side
				// (legacy `toJs` models "no signature" as `{ id: 'none' }`).
				signatureTemplateId,
				templateReference,
				options: {
					sendSingleEmail: true,
					saveAsNote,
					// Only meaningful when saved as a note; harmless otherwise.
					noteVisibility,
					scheduledSend: { criteria: ScheduleCriteria.Immediately },
				},
			};

			const formData = new FormData();
			formData.append('value', JSON.stringify(compose));

			const result = await userSession.webServiceHelper.callAsync<ISendEmailResponse>(
				ImpersonationBroker.composeApiUrl({ urlPath: 'email' }),
				'POST',
				formData
			);
			// `callAsync` throws on 4xx/5xx; defensive against a 200 with no body.
			if (!result) {
				throw new Error('useSendEmail: email send returned no response');
			}
			return result;
		},
		onSuccess: (_data, variables) => {
			// The send is saved as a note on the lead — refresh so it shows up.
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(variables.companyId) });
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.leadHistory(variables.companyId) });
		},
		onError: error => {
			// No MutationCache in this app (see packages/main/CLAUDE.md) — surface
			// failures explicitly.
			toast.error(error instanceof Error && error.message ? error.message : 'Failed to send email. Please try again.');
		},
	});
}
